import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/**
 * Показывается принудительно, пока у администратора стоит mustChangePassword —
 * учётка из сида не должна остаться с паролем по умолчанию.
 */
export default function ChangePasswordPage() {
  const { logout } = useAuth();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (newPassword !== repeat) {
      setError('Пароли не совпадают');
      return;
    }

    setPending(true);
    try {
      await api.post('/admin/auth/change-password', { currentPassword, newPassword });
      // Сервер погасил все сессии — заходим заново с новым паролем
      setDone(true);
      setTimeout(() => void logout(), 1500);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  const field =
    'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900 dark:border-neutral-700 dark:bg-neutral-950 dark:focus:border-neutral-300';

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-5 rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
      >
        <div>
          <h1 className="text-xl font-semibold">Смена пароля</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Пароль по умолчанию нужно заменить перед началом работы.
          </p>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Текущий пароль</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            className={field}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Новый пароль</span>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNext(e.target.value)}
            className={field}
          />
          <span className="text-xs text-neutral-500">Минимум 8 символов, буква и цифра</span>
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Повторите пароль</span>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            className={field}
          />
        </label>

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400">
            {error}
          </p>
        )}

        {done && (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
            Пароль изменён. Все сессии завершены — войдите заново.
          </p>
        )}

        <button
          type="submit"
          disabled={pending || done}
          className="w-full rounded-lg bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800 disabled:opacity-50 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200"
        >
          {pending ? 'Сохраняем…' : 'Сменить пароль'}
        </button>
      </form>
    </div>
  );
}
